import { useState } from "react"

import {
  useSetFramingAnswer,
  useSetFramingAnswerForAllSlices,
} from "~/components/demand-2/shared/FramingAnswerPicker"
import { STRATEGY_REQUIRES } from "~/components/demand-2/shared/strategyPermission"
import { useSalesSlices } from "~/components/demand-2/shared/useSalesSlices"
import { useFraming } from "~/components/demand-2/useDemandIntents"
import { ConfirmationDialog } from "~/components/reusable/ConfirmationDialog"
import {
  type AnswerScope,
  effectiveAnswerFor,
  framingOptionLabel,
  type FramingAnswers,
  type FramingSlice,
  isAllSlice,
  scopeWriteKey,
  type SliceAnswerQuestion,
} from "~/utils/demandFraming"
import { formatCount, formatList } from "~/utils/formatting"

/** The slices whose own answer a collection-wide `next` would replace with a different one. */
const overriddenSlices = (
  framing: FramingAnswers,
  question: SliceAnswerQuestion,
  slices: readonly { slice: FramingSlice; label: string }[],
  next: string,
): string[] =>
  slices
    .filter(({ slice }) => {
      const own = effectiveAnswerFor(framing, question, { kind: "whole", slice })
      return !own.inherited && own.answer !== next
    })
    .map(({ label }) => label)

const MAX_LISTED = 3

const overrideDescription = (labels: string[], noun: string): string => {
  const shown = labels.slice(0, MAX_LISTED)
  const rest = labels.length - shown.length
  const names = formatList(rest > 0 ? [...shown, `${rest} more`] : shown)
  const verb =
    labels.length === 1 ? "answers differently on its own" : "answer differently on their own"
  return `${formatCount(labels.length, noun)} ${verb}: ${names}. Confirming applies this answer to every ${noun}.`
}

/**
 * A collection answer covers every sales slice: one that would replace slices' own different
 * answers asks first, then writes for all of them at once. Any other write goes straight through.
 * Render `dialog` next to the picker; `pending` holds the picker while the slices load or the
 * write lands. `labelFor` names an answer as the picker does, where that differs by kind.
 */
export const useAllSlicesGuard = ({
  question,
  collectionId,
  scope,
  labelFor,
}: {
  question: SliceAnswerQuestion
  collectionId: number
  scope: AnswerScope
  labelFor?: (answer: string) => string | null
}) => {
  const framing = useFraming()
  const salesSlices = useSalesSlices()
  const set = useSetFramingAnswer(collectionId)
  const setForAll = useSetFramingAnswerForAllSlices()
  const [pending, setPending] = useState<{ answer: string; overridden: string[] } | null>(null)
  const isCollection = scope.kind === "whole" && isAllSlice(scope.slice)
  const hasOwnSlices = (framing.sliceAnswers ?? []).some(
    (r) => r.question === question && r.variantId === "",
  )
  // The slices' own answers can't be compared until they load, so the pick waits.
  const slicesLoading = isCollection && hasOwnSlices && salesSlices == null
  const noun = salesSlices?.noun ?? "combination"

  const write = (next: string | null) => {
    if (slicesLoading) return
    if (!isCollection || next == null || !hasOwnSlices) {
      set.mutate({ collectionId, question, answer: next, ...scopeWriteKey(scope) })
      return
    }
    const overridden = overriddenSlices(framing, question, salesSlices?.slices ?? [], next)
    if (overridden.length > 0) setPending({ answer: next, overridden })
    else setForAll.mutate({ collectionId, question, answer: next })
  }

  const label = pending
    ? (labelFor?.(pending.answer) ?? framingOptionLabel(question, pending.answer))
    : null
  const dialog = (
    <ConfirmationDialog
      open={pending != null}
      onOpenChange={(open: boolean) => {
        if (!open) setPending(null)
      }}
      requires={STRATEGY_REQUIRES}
      title={`Use “${label ?? ""}” for every ${noun}?`}
      confirmText="Use for all"
      description={overrideDescription(pending?.overridden ?? [], noun)}
      onConfirm={() => {
        if (pending) setForAll.mutate({ collectionId, question, answer: pending.answer })
        setPending(null)
      }}
    />
  )
  return { write, dialog, pending: set.isPending || setForAll.isPending || slicesLoading }
}
